// 📄 pages/Admin/sections/GuiaIntegracionSection.tsx
//
// "Guía de Integración Estándar" (2026-08-26) — CRUD del catálogo global
// de checklists de configuración por combinación (Solución, ERP), ver
// guiasIntegracionService.ts. Mismo patrón que BadiCatalogSection.tsx,
// sin el sembrado inicial (este contenido es autoría manual del Admin,
// no tiene sentido un dataset de ejemplo).
import { useEffect, useState } from 'react';
import { Plus, RefreshCw, BookOpenCheck, Pencil, Trash2 } from 'lucide-react';
import { GuiaIntegracion, getGuiasIntegracion, deleteGuiaIntegracion } from '../../../services/guiasIntegracionService.ts';
import { SOLUCION_OPTIONS } from '../../Proyectos/solucionesConfig.ts';
import { labelTipoErp } from '../../Proyectos/erpConfig.ts';
import GuiaIntegracionFormModal from '../components/GuiaIntegracionFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

const labelSolucion = (slug: string): string =>
  SOLUCION_OPTIONS.find((s) => s.value === slug)?.label || slug;

export default function GuiaIntegracionSection() {
  const [guias, setGuias] = useState<GuiaIntegracion[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<GuiaIntegracion | null>(null);
  const [aEliminar, setAEliminar] = useState<GuiaIntegracion | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await getGuiasIntegracion();
      data.sort((a, b) => labelSolucion(a.solucion).localeCompare(labelSolucion(b.solucion)) || a.erpTipo.localeCompare(b.erpTipo));
      setGuias(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditando(null); setModalOpen(true); };
  const openEdit = (guia: GuiaIntegracion) => { setEditando(guia); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await deleteGuiaIntegracion(aEliminar.solucion, aEliminar.erpTipo);
      setAEliminar(null);
      cargar();
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Guías de Integración Estándar</h1>
          <p className="text-sm text-gray-400 mt-1">Checklist de configuración por combinación Solución + ERP — se muestra automáticamente en cada proyecto que coincida.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Nueva Guía
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando guías...</p>
        </div>
      ) : guias.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <BookOpenCheck className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Todavía no hay guías creadas.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {guias.map((guia) => (
            <div key={guia.id} className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="inline-flex items-center gap-1.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                    {labelSolucion(guia.solucion)} · {labelTipoErp(guia.erpTipo)}
                  </span>
                  <h3 className="text-white font-medium mt-2">{guia.nombre}</h3>
                  {guia.descripcion && <p className="text-sm text-gray-400 mt-1">{guia.descripcion}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(guia)} title="Editar" className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setAEliminar(guia)} title="Eliminar" className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">{guia.pasos.length} paso{guia.pasos.length !== 1 ? 's' : ''}</p>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <GuiaIntegracionFormModal guia={editando} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta guía?"
          message={`"${aEliminar.nombre}" se eliminará del catálogo global — los proyectos que ya la tenían visible dejarán de verla. El progreso ya marcado en cada proyecto no se borra (queda huérfano, sin efecto).`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
